import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BusinessService } from '../business.service';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { InfoService } from '@app/core/info/info.service';
import { SessionStorageService } from '@app/core/local-storage/local-storage.service';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { WebDentificationComponent } from './web-dentification/web-dentification.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'cloud-identification',
  templateUrl: './identification.component.html',
  styleUrls: ['./identification.component.scss']
})
export class IdentificationComponent implements OnInit, OnDestroy {
  identificatorForm!: FormGroup;
  egnOptions = ['EGN', 'Passport'];
  isSubmitting: boolean = false;
  hasIdentification: boolean = false;
  hasWebIdentification: boolean = false;
  businessAccountId: any;
  callbackId: any;
  isFromOtc: boolean = false;
  selectedTabIndex: number = 0;
  private pollingSubscription: Subscription | null = null;
  groups: any[] = [];

  @ViewChild('webIdent') webIdent!: WebDentificationComponent;
  
  constructor(
    private router: Router,
    private formBuilder: FormBuilder,
    private businessService: BusinessService,
    public info: InfoService,
    private sessionStorageService: SessionStorageService,
    private translateService: TranslateService,
  ) {
    const navigation = this.router.getCurrentNavigation()?.extras?.state || { isFromOtc: false };
    this.isFromOtc = navigation.isFromOtc;
  }

  ngOnInit(): void {
    this.businessAccountId = this.sessionStorageService.getItem("businessAccountId");
    this.buildForm();
    this.initTabs();

    if (this.isFromOtc) {
      this.selectedTabIndex = 1;
      setTimeout(() => {
        this.webIdent.loadWebIdentificator();
      }, 0);
    }
  }

  buildForm() {
    this.identificatorForm = this.formBuilder.group({
      personalIdType: ['EGN'],
      personalId: ['', [
        Validators.required,
        Validators.pattern(/^[0-9]{10}$/)
      ]],
      email: ['', [Validators.email, Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)]],
      phone: ['', [Validators.pattern(/^(?!\+?3590)(?!\+0)(\+[0-9]{7,15})$/)]],
      identificationReason: ['',
      {
        validators: [
          Validators.required,
          Validators.pattern(/^(?! *$)[A-Za-zА-Яа-яЁё0-9 \p{P}]+$/u),
          Validators.maxLength(500)]
      }],
    }, {
      validators: this.atLeastOneContactRequired
    });
  }

  initTabs(): void {
    const employeeId = this.sessionStorageService.getItem("loginEventData")?.userData?.cmrId;
    this.businessService.getEmployeesServices(this.businessAccountId, employeeId).subscribe(res => {
      const groups = res.data;
      this.hasIdentification = this.hasIdentificationService(groups, "IDENTIFICATION");
      this.hasWebIdentification = this.hasIdentificationService(groups, "WEB_IDENTIFICATION");
      if (!this.isFromOtc) {
        this.selectedTabIndex = this.hasIdentification ? 0 : 1;
      }
    });
  }

  hasIdentificationService(groups: any[], service: string): boolean {
    if (!groups || !Array.isArray(groups)) {
      return false;
    }

    return groups.some((group: any) => {
      return group.serviceType === service;
    });
  }

  atLeastOneContactRequired(control: AbstractControl) {
    const email = control.get('email')?.value;
    const phone = control.get('phone')?.value;

    if (!email && !phone) {
      return { atLeastOneRequired: true };
    }
    return null;
  }

  onSubmitIdentificatorForm() {
    if (this.isSubmitting) return;

    this.isSubmitting = true;
    const formData = Object.fromEntries(
      Object.entries(this.identificatorForm.value).map(([key, value]) => [key, value === '' ? null : (value as string).trim()])
    );
    
    this.businessService.requestIdentification(formData, this.businessAccountId)
      .subscribe((res: any) => {
        this.identificatorForm.reset({
          personalIdType: 'EGN'
        });
        this.identificatorForm.markAsUntouched();
        this.identificatorForm.markAsPristine();
        Object.keys(this.identificatorForm.controls).forEach(key => {
          const control = this.identificatorForm.get(key);
          if (control) {
            control.markAsUntouched();
            control.markAsPristine();
            control.setErrors(null);
          }
        });
        this.isSubmitting = false;
        if (res.status >= 200 && res.status < 300) {
          this.info.success(this.translateService.instant('businessTexts.identificationTable.requestSuccessfully'))
        } else if (res.status == 400) {
          this.info.error(this.translateService.instant('businessTexts.identificationTable.badRequestError'))
        } else {
          this.info.error(this.translateService.instant('businessTexts.identificationTable.requestError'));
        }
      })
  }
  
  onTabChange(event: MatTabChangeEvent): void {
    // if (event.index === 1 && this.webIdent) {
    //   this.webIdent.loadWebIdentificator();
    // }
     if (event.index === 1) {
      this.webIdent.loadWebIdentificator();
    }
  }

  ngOnDestroy(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
  }
}
